import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Clock, Package } from 'lucide-react';
import { supabase } from '../supabase/client';
import { Order } from '../utils/orderService';
import {
  getStatusConfig,
  getPayMethodLabel,
  getDurationLabel,
  calcActualPrice,
  calcOriginalPrice,
  getSourceLabel,
} from '../utils/orderUtils';
import { formatDateTime } from '../utils/timeUtils';

export default function OrderDetail() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (orderId) loadOrder(orderId);
  }, [orderId]);

  const loadOrder = async (id: string) => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('student_orders')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (data) {
        // 获取用户信息
        let userName = '未知用户';
        let userPhone = '-';
        if (data.user_id) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('username, phone')
            .eq('id', data.user_id)
            .maybeSingle();
          if (profile) {
            userName = profile.username || '未知用户';
            userPhone = profile.phone?.includes('@') ? profile.phone.split('@')[0] : profile.phone || '-';
          }
        }

        const d = data as any;
        setOrder({
          id: d.id,
          userId: d.user_id,
          userName,
          userPhone,
          orderNo: d.order_no || d.id,
          orgId: d.org_id || '',
          orgName: d.org_name || d.organization || '-',
          catId: d.cat_id || '',
          catName: d.cat_name || d.category || '-',
          levelId: d.level_id || '',
          levelText: d.level_text || '-',
          duration: d.duration_type as Order['duration'],
          durationLabel: getDurationLabel(d.duration_type),
          originalPrice: Number(d.original_price) || Number(d.price) || 0,
          price: d.actual_price != null ? Number(d.actual_price) : Number(d.price) || 0,
          actualPrice: d.actual_price != null ? Number(d.actual_price) : undefined,
          pointsDeducted: d.points_deducted || undefined,
          deductionAmount: d.deduction_amount ? Number(d.deduction_amount) : undefined,
          status: d.status as Order['status'],
          payMethod: d.pay_method as Order['payMethod'] || undefined,
          payTime: d.paid_at,
          expireDate: d.expire_date || '-',
          createdAt: d.created_at,
          updatedAt: d.updated_at,
          source: (d.package_source || d.source || 'self') as Order['source'],
        });
      }
    } catch (err) {
      console.error('加载订单详情失败:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-500">加载中...</span>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <Package size={48} className="text-gray-300 mb-4" />
        <p className="text-gray-500 mb-4">订单不存在或已被删除</p>
        <button onClick={() => navigate(-1)} className="text-blue-600 hover:underline">返回上一页</button>
      </div>
    );
  }

  const statusConfig = getStatusConfig(order.status);
  const actualPrice = calcActualPrice(order);
  const originalPrice = calcOriginalPrice(order);

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-3xl mx-auto">
        {/* 返回按钮 */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>返回</span>
        </button>

        {/* 订单状态卡片 */}
        <div className={`rounded-xl p-6 mb-6 ${statusConfig.bgColor} border`}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">订单详情</h1>
              <p className="text-sm text-gray-600 font-mono">{order.orderNo}</p>
            </div>
            <span className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-semibold ${statusConfig.bgColor} ${statusConfig.color} border`}>
              {statusConfig.label}
            </span>
          </div>
        </div>

        {/* 订单信息 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="font-semibold text-gray-900">订单信息</h2>
          </div>
          <div className="p-6 space-y-4">
            <InfoRow label="学员姓名" value={order.userName} />
            <InfoRow label="手机号" value={order.userPhone} />
            <InfoRow label="套餐名称" value={`${order.orgName}-${order.catName}-${order.levelText}`} />
            <InfoRow label="套餐类型" value={getDurationLabel(order.duration)} />
            <InfoRow label="渠道来源" value={getSourceLabel(order.source)} />
          </div>
        </div>

        {/* 金额信息 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="font-semibold text-gray-900">金额信息</h2>
          </div>
          <div className="p-6 space-y-4">
            <InfoRow label="订单总额" value={`¥${originalPrice.toFixed(2)}`} />
            {order.pointsDeducted && order.pointsDeducted > 0 && (
              <InfoRow
                label="积分抵扣"
                value={`-${order.pointsDeducted}分 (约¥${(order.deductionAmount || 0).toFixed(2)})`}
                valueClass="text-orange-600"
              />
            )}
            <div className="flex justify-between items-center pt-3 border-t border-gray-100">
              <span className="text-sm font-medium text-gray-700">实付金额</span>
              <span className="text-xl font-bold text-blue-600">¥{actualPrice.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* 支付信息 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="font-semibold text-gray-900">支付信息</h2>
          </div>
          <div className="p-6 space-y-4">
            <InfoRow label="支付方式" value={getPayMethodLabel(order)} />
            <InfoRow label="创建时间" value={formatDateTime(order.createdAt)} />
            <InfoRow label="支付时间" value={order.payTime ? formatDateTime(order.payTime) : '-'} />
            <InfoRow label="到期时间" value={order.expireDate !== '-' ? order.expireDate : '-'} />
          </div>
        </div>

        {/* 退款信息（仅已退款订单显示） */}
        {order.status === 'refunded' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <h2 className="font-semibold text-gray-900">退款信息</h2>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-800">退款申请已处理</div>
                  <div className="text-xs text-gray-500">{formatDateTime(order.updatedAt)}</div>
                </div>
              </div>
              <div className="ml-4 pl-4 border-l-2 border-green-200 space-y-3">
                <InfoRow label="退款金额" value={`¥${actualPrice.toFixed(2)}`} valueClass="font-bold text-red-600" />
                <InfoRow label="退款去向" value="原路退回" />
              </div>
              <div className="flex items-center gap-3 pt-2">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-800">退款到账中</div>
                  <div className="text-xs text-gray-500">预计1-3个工作日到账</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value, valueClass = '' }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-sm text-gray-500">{label}</span>
      <span className={`text-sm text-gray-900 ${valueClass}`}>{value}</span>
    </div>
  );
}
